import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/browser";
import PropTypes from "prop-types";
import { useLinks, useSubmitInterviewAnswer, useVideoPresentation } from "hooks";
import { INTERVIEW_MEDIA_DEVICES, INTERVIEW_MEDIA_DEVICES_STATUS, INTERVIEW_PHASE } from "constants/index";
import { Grid } from "components/Containers";
import {
  IntroStep,
  PermissionStep,
  SetupStep,
  QuestionStep,
  ResumeInterviewStep,
  FinishedStep,
} from "components/VideoPresentation";
import { directUpload } from "utils";

/**
 * VideoInterviewFlow
 *
 * @param {string}  introVideoUrl
 * @param {string}  status
 * @param {Array}   questions
 * @param {string}  interviewId
 * @param {string}  profileId
 */
const VideoInterviewFlow = ({ introVideoUrl, status, questions, answers, interviewId, profileId, ...props }) => {
  const [phase, setPhase] = useState(
    status === "completed"
      ? INTERVIEW_PHASE.finished
      : introVideoUrl
      ? INTERVIEW_PHASE.intro
      : INTERVIEW_PHASE.permission
  );
  const [introWatchedSeconds, setIntroWatchedSeconds] = useState(0);

  const [devices, setDevices] = useState({
    cameras: [],
    microphones: [],
    speakers: [],
  });

  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [selectedMicId, setSelectedMicId] = useState(null);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState(null);

  const [stream, setStream] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [retryInfo, setRetryInfo] = useState();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completedAnswers, setCompletedAnswers] = useState([]);
  const [isResuming, setIsResuming] = useState(false);
  const [resumeIndex, setResumeIndex] = useState(0);
  const [resumeChecked, setResumeChecked] = useState(false);

  const { handleSubmitInterviewAnswer, loading: submittingAnswer, error: uploadError } = useSubmitInterviewAnswer();
  const { getMediaConstraints } = useVideoPresentation();

  const links = useLinks();

  useEffect(() => {
    if (answers.length > 0) {
      setCompletedAnswers(
        answers.map((ans) => ({
          questionId: ans.id,
          videoUrl: ans.videoUrl,
          thumbnailUrl: ans.thumbnailUrl,
        }))
      );
    }
  }, [answers]);

  useEffect(() => {
    if (resumeChecked) return;
    if (!questions.length) return;

    const tryResume = async () => {
      if (answers.length === 0 || answers.length === undefined) return;

      const completed = answers.map((a) => a.id);
      const nextIndex = questions.findIndex((q) => !completed.includes(q.id));

      if (nextIndex === -1) {
        setPhase(INTERVIEW_PHASE.finished);
        return;
      }

      setResumeIndex(nextIndex);
      setIsResuming(true);

      const hasPerms = await checkMediaPermission();

      if (hasPerms) {
        setPhase(INTERVIEW_PHASE.resume); // <-- Only show the resume card
      } else {
        setPhase(INTERVIEW_PHASE.permission);
      }

      setResumeChecked(true);
    };

    tryResume();
  }, []);

  useEffect(() => {
    if (phase === INTERVIEW_PHASE.finished) {
      stopStream();
    }
  }, [phase]);

  const handleUpload = async (file) => {
    try {
      setUploadProgress(0);

      const blob = await directUpload({
        file,
        uploadUrl: links.direct_uploads,
        onProgress: setUploadProgress,
        maxRetries: 3,
        onRetry: (attempt, maxRetries) => {
          setRetryInfo({ attempt, maxRetries });
        },
      });

      const response = await handleSubmitInterviewAnswer(
        profileId,
        interviewId,
        questions[currentQuestionIndex].id,
        blob.signed_id
      );

      setUploadProgress(0);
      setRetryInfo(null);

      setCompletedAnswers((prev) => [
        ...prev,
        {
          questionId: questions[currentQuestionIndex].id,
          videoUrl: response.answers.find((ans) => ans.id === questions[currentQuestionIndex].id)?.videoUrl,
        },
      ]);

      if (currentQuestionIndex + 1 < questions.length) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else {
        setPhase(INTERVIEW_PHASE.finished);
      }
    } catch (err) {
      Sentry.captureException(err);
      alert("Upload failed. Please try again.");
    }
  };

  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const loadDevices = async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        cameras: allDevices.filter((d) => d.kind === "videoinput"),
        microphones: allDevices.filter((d) => d.kind === "audioinput"),
        speakers: allDevices.filter((d) => d.kind === "audiooutput"),
      });

      if (!selectedCameraId) {
        const firstCam = allDevices.find((d) => d.kind === "videoinput");
        if (firstCam) setSelectedCameraId(firstCam.deviceId);
      }
      if (!selectedMicId) {
        const firstMic = allDevices.find((d) => d.kind === "audioinput");
        if (firstMic) setSelectedMicId(firstMic.deviceId);
      }
      if (!selectedSpeakerId) {
        const firstSpeaker = allDevices.find((d) => d.kind === "audiooutput");
        if (firstSpeaker) setSelectedSpeakerId(firstSpeaker.deviceId);
      }
    } catch (err) {
      alert("Unable to access media devices. Please check your permissions.");
    }
  };

  const handlePermissionsGranted = async (stream) => {
    setStream(stream);
    await loadDevices();

    if (isResuming) {
      // User was resuming — go straight to question
      setCurrentQuestionIndex(resumeIndex);
      setPhase(INTERVIEW_PHASE.question);
    } else {
      // Normal new interview flow
      setPhase(INTERVIEW_PHASE.setup);
    }
  };

  const handleIntroProgress = (seconds) => {
    setIntroWatchedSeconds(seconds);
  };

  const handleStartInterview = () => {
    setPhase(INTERVIEW_PHASE.permission);
  };

  const handleResumeInterview = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(getMediaConstraints(selectedCameraId, selectedMicId));

      setStream(stream);
      await loadDevices(); // load device list again

      setCurrentQuestionIndex(resumeIndex);
      setPhase(INTERVIEW_PHASE.question); // go straight to question
    } catch (err) {
      // If resume fails, ask for permission again
      setPhase(INTERVIEW_PHASE.permission);
    }
  };

  const handleSetupComplete = () => {
    if (!stream) {
      alert("Camera and microphone are required to start the interview.");
      return;
    }
    setPhase(INTERVIEW_PHASE.question);
  };

  const handleChangeDevices = async (cameraId, micId) => {
    try {
      // Stop old stream
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }

      const newStream = await navigator.mediaDevices.getUserMedia(getMediaConstraints(cameraId, micId));

      setStream(newStream);
      setSelectedCameraId(cameraId);
      setSelectedMicId(micId);
    } catch (err) {
      alert("Unable to switch devices. Please try again.");
    }
  };

  const handleAnswerRecorded = async (blob) => {
    await handleUpload(blob);
  };

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <Grid>
      <Grid.col start={1} end={13}>
        {phase === INTERVIEW_PHASE.intro && introVideoUrl && (
          <IntroStep
            introVideoUrl={introVideoUrl}
            onProgress={handleIntroProgress}
            canStart={introWatchedSeconds >= 3}
            onStart={handleStartInterview}
            {...props}
          />
        )}

        {phase === INTERVIEW_PHASE.permission && (
          <PermissionStep
            onGranted={handlePermissionsGranted}
            showBack={introVideoUrl}
            onBack={() => setPhase(INTERVIEW_PHASE.intro)}
          />
        )}

        {phase === INTERVIEW_PHASE.setup && (
          <SetupStep
            stream={stream}
            devices={devices}
            selectedCameraId={selectedCameraId}
            selectedMicId={selectedMicId}
            selectedSpeakerId={selectedSpeakerId}
            onChangeDevices={handleChangeDevices}
            onChangeSpeaker={setSelectedSpeakerId}
            onContinue={handleSetupComplete}
          />
        )}

        {phase === INTERVIEW_PHASE.question && currentQuestion && (
          <QuestionStep
            stream={stream}
            question={currentQuestion}
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={questions.length}
            onAnswerRecorded={handleAnswerRecorded}
            uploading={submittingAnswer || uploadProgress > 0}
            uploadError={uploadError}
            uploadProgress={uploadProgress}
            retryInfo={retryInfo}
          />
        )}

        {phase === INTERVIEW_PHASE.finished && <FinishedStep questions={questions} answers={completedAnswers} />}

        {phase === INTERVIEW_PHASE.resume && (
          <ResumeInterviewStep
            answeredCount={answers.length}
            totalCount={questions.length}
            onResume={handleResumeInterview}
          />
        )}
      </Grid.col>
    </Grid>
  );
};

/**
 * checkMediaPermission
 */
async function checkMediaPermission() {
  try {
    const status = await navigator.permissions.query({ name: INTERVIEW_MEDIA_DEVICES.camera });
    const micStatus = await navigator.permissions.query({ name: INTERVIEW_MEDIA_DEVICES.microphone });

    return (
      status.state === INTERVIEW_MEDIA_DEVICES_STATUS.granted &&
      micStatus.state === INTERVIEW_MEDIA_DEVICES_STATUS.granted
    );
  } catch (err) {
    // Permissions API fails on Safari — fallback
    return false;
  }
}

VideoInterviewFlow.propTypes = {
  introVideoUrl: PropTypes.string,
  questions: PropTypes.array.isRequired,
  answers: PropTypes.array.isRequired,
  interviewId: PropTypes.string.isRequired,
  profileId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
};

export default VideoInterviewFlow;
